import { computed, inject } from 'vue';
import { IKAgentsSelected } from '@/views/agents/components/agents-table/agents-table.InjectionKeys';
import { Agent, Agent_SortField, Agent_SortOrder, Agent_Status, AgentsQuery, useCommonDataLazyQuery, useCommonDataQuery, useSettingsPageLinuxQuery } from '@/graphql/codegen/generated/graphql';
import { useAgentsColumnsManagement } from '@/views/agents/agents.helpers';
import { TableEditorUnified } from '@/views/agents/components/agents-table/types/agents-table.types';
import { useUserRolePermissions } from '@/services/UserRolePermissions/UserRolePermissions';
import { IKAgentsPersistentData, IKAgentsQueryFilter } from '@/views/agents/agents.injectionKeys';
import { CLOUD_TAG_AGENT_TABLE_COLUMNS } from '@/views/agents/components/agents-table/agents-table.const';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { useLazyQueryWithCoordinator } from '@/services/graphql.utils';
import { OsType } from '@/modules/platformAuthentication/platformAuthentication.const';
import { useOsService } from '@/services/OsService/OsService';
import { TableState } from './agents-table.types';

export const useAgentsTableUtils = () => {
  const agentsPagePersistentData = inject(IKAgentsPersistentData);
  const selectedAgents = inject(IKAgentsSelected);
  const agentsQueryFilter = inject(IKAgentsQueryFilter);
  const userRolePermissions = useUserRolePermissions();
  const columnsData = useAgentsColumnsManagement()?.tableEditor as TableEditorUnified;
  const coordinatorsService = inject(IKCoordinatorsService);
  const osService = useOsService();
  const GQL_agentsPageCommonData = useLazyQueryWithCoordinator(useCommonDataLazyQuery, { coordinatorId: coordinatorsService?.selectedCoordinator.value || '' });

  const selectAgent = (newValue: boolean | null, agent: Agent | null) => {
    if (newValue) {
      const selectedAgentNamesPayload = new Set([...(selectedAgents?.selectedAgentIds.value || []), agent?.id || '']);
      selectedAgents?.selectByIds(selectedAgentNamesPayload);
    } else {
      const selectedAgentNamesPayload = new Set(selectedAgents?.selectedAgentIds.value || []);
      selectedAgentNamesPayload.delete(agent?.id || '');
      selectedAgents?.selectByIds(selectedAgentNamesPayload);
    }
  };

  const isCloudActivated = computed(() => GQL_agentsPageCommonData?.result?.value?.ibManagerEntities?.settingsMetadata?.isCloudActivated);
  const osType = computed(() => osService?.osType.value || OsType.Windows);
  const isCloudColumn = (columnName: string) => CLOUD_TAG_AGENT_TABLE_COLUMNS.includes(columnName);

  const updateColumnWidth = (column: { columnName: string; width: number }) => {
    const columnNewData = columnsData?.get[osType.value].value.columnsOrder?.map(orderedColumn => {
      if (orderedColumn.name === column.columnName) {
        return { ...orderedColumn, width: column.width };
      }
      return orderedColumn;
    });
    // Set the new column width
    columnsData?.set[osType.value]({
      columnsOrder: [...(columnNewData || [])],
      columnsFilter: { ...columnsData?.get[osType.value].value.columnsFilter },
    });
  };

  const handlePageResize = async (pageSize: number) => {
    agentsQueryFilter?.set({ ...agentsQueryFilter?.get.value, paging: { pageNumber: 1, pageSize } });
    agentsPagePersistentData?.setPaging({ pageSize });
  };

  const handlePageSorting = (field: Agent_SortField, sortOrder: Agent_SortOrder, secondarySortField?: Agent_SortField) => {
    agentsQueryFilter?.set({ ...agentsQueryFilter?.get.value, sort: { field, order: sortOrder }, secondarySortField });
    const persistentPayload = {
      sort: {
        field,
        order: sortOrder,
      },
      secondarySortField,
    };
    agentsPagePersistentData?.setSorting(persistentPayload);
  };

  const isCloudAgentInStatusStoppedOrUpdating = (agent: Agent | null) => agent?.metadata?.isCloud && (agent?.status === Agent_Status.Stopped || agent?.status === Agent_Status.Updating);
  const isCloudAgentInStatusStopped = (agent: Agent | null) => agent?.metadata?.isCloud && agent?.status === Agent_Status.Stopped;

  const shouldOpenContextMenu = () => selectedAgents && userRolePermissions.authorize?.value?.agents.enabled.actions;

  const handleAgentsSelection = (tableState: TableState, agentsQueryResult: AgentsQuery['agents']) => {
    if (!selectedAgents?.selectedAgentIds.value) return;
    const updatedSelectedAgents =
      agentsQueryResult?.agents?.filter(agent => tableState?.selection?.items.has(agent?.id || '') && !isCloudAgentInStatusStoppedOrUpdating(agent)).map(agent => agent?.id as string) || [];
    if (updatedSelectedAgents.length && updatedSelectedAgents.every(agentId => selectedAgents?.selectedAgentIds.value.has(agentId))) {
      selectedAgents?.clearSelection();
    } else if (tableState.selection?.items) {
      selectedAgents?.selectByIds(new Set(updatedSelectedAgents));
    }
  };

  const getContentForCloudStoppedAgents = (agent: Agent | null, value: any) => (isCloudAgentInStatusStopped(agent) ? value : '');

  const getCloudAgentVolumeSize = (agent: Agent | null) => {
    if (isCloudAgentInStatusStopped(agent)) {
      return `${agent?.cloud?.volumeSize || 0} GB`;
    }
    return '';
  };

  const getCloudCacheSize = (agent: Agent | null) => {
    if (isCloudAgentInStatusStopped(agent)) {
      return `${(agent?.cloud?.cacheSize || 0) * 1000} MB`;
    }
    return '';
  };

  const GQL_CommonData = useCommonDataQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));
  const GQL_useSettingsPageLinuxQuery = useSettingsPageLinuxQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));

  const featureFlags = computed(() => (GQL_CommonData?.result?.value?.ibManagerEntities?.featureFlags ? JSON.parse(GQL_CommonData?.result?.value?.ibManagerEntities?.featureFlags) : null));
  const getAgentSettingsUrl = (agent: Agent, byIp = false) => {
    if (agent.metadata?.isCloud) {
      return;
    }
    let coordinatorPortTunneling = GQL_useSettingsPageLinuxQuery?.result?.value?.ibManagerEntities?.settings?.linux?.network?.coordinatorSSLPort || '';
    const agentPortTunneling = agent.securedPort;
    const isAgentTunnelingEnabled = GQL_useSettingsPageLinuxQuery?.result?.value?.ibManagerEntities?.settings?.linux?.network?.useCoordinatorAgentTunnel;
    if (isAgentTunnelingEnabled && featureFlags.value?.networking?.reverse_proxy) {
      coordinatorPortTunneling = '';
    }
    const { hostname, protocol } = new URL(window.location.href);
    const urlAgentTunneling = `${protocol}//${hostname}:${coordinatorPortTunneling}/agents/${byIp ? agent.ip : agent.name || agent.ip}:${agentPortTunneling}/incredibuild/`;
    const agentPortNoTunneling = GQL_useSettingsPageLinuxQuery?.result?.value?.ibManagerEntities?.settings?.linux?.network?.ssl ? agent.securedPort : agent.port;
    const urlProtocol = GQL_useSettingsPageLinuxQuery?.result?.value?.ibManagerEntities?.settings?.linux?.network?.ssl ? 'https:' : 'http:';
    const urlAgentNoTunneling = `${urlProtocol}//${byIp ? agent.ip : agent.name || agent.ip}:${agentPortNoTunneling}`;
    return isAgentTunnelingEnabled ? urlAgentTunneling : urlAgentNoTunneling;
  };

  const isAgentOS32Bit = (agent: Agent | null) => agent?.osVersion?.includes('(32-bit)');

  return {
    selectAgent,
    updateColumnWidth,
    shouldOpenContextMenu,
    handlePageResize,
    isCloudAgentInStatusStoppedOrUpdating,
    isCloudAgentInStatusStopped,
    handleAgentsSelection,
    isCloudActivated,
    isCloudColumn,
    getContentForCloudStoppedAgents,
    getCloudAgentVolumeSize,
    getCloudCacheSize,
    getAgentSettingsUrl,
    isAgentOS32Bit,
    handlePageSorting,
  };
};
